import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useAutoDismissNotice } from '../../hooks/useAutoDismissNotice';
import { useProjectProcessTypes } from '../../hooks/useProjectProcessTypes';
import { importToolbox } from '../../features/toolbox/toolboxSlice';
import { generateId } from '../../utils/geometry';
import { CLASS_LABELS, COMPONENT_SCHEMAS, TOOLBOX_ITEMS } from '../../constants';
import {
  TOOLBOX_BOM_UNITS,
  type ComponentField,
  type ElementType,
  type ToolboxBomLine,
  type ToolboxBomUnit,
  type ToolboxCustomField,
  type ToolboxItem as ToolboxItemType,
} from '../../types';
import {
  createToolboxProductType,
  fetchToolboxCatalog,
  linkedStraightTrackTwinId,
  newToolboxToolId,
  resetToolboxCatalog,
  saveToolboxCatalog,
  syncLinkedStraightTrackTools,
  type ToolboxProductType,
  type ToolboxStandardParam,
} from '../../utils/toolboxApi';
import {
  behaviorFromIconTemplate,
  resolveToolboxTemplateByIcon,
} from '../../utils/toolboxIconBehavior';
import { PoweredByPathfinder } from '../common/PoweredByPathfinder';

interface ToolboxSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

type ProductTypeDef = {
  id: string;
  label: string;
  color: string;
  elementTypes: ElementType[];
  /** Server-marked system categories cannot be removed from the sidebar. */
  isSystem?: boolean;
};

type EquipmentGroup = {
  id: string;
  label: string;
  icon: string;
  items: ToolboxItemType[];
};

type DrawerTab = 'config' | 'bom';
type AddToolTab = 'setup' | 'bom';
type IconSource = 'library' | 'upload';

type ConfirmTone = 'danger' | 'warning';
type NoticeTone = 'success' | 'error';
type ToolboxNotice = { message: string; tone: NoticeTone };

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel: string;
  tone: ConfirmTone;
  onConfirm: () => void;
};

const ALL_TOOLS_ID = 'all-tools';

const PRODUCT_TYPES: ProductTypeDef[] = [
  {
    id: 'overhead-rail',
    label: 'OVERHEAD RAIL',
    color: '#2563eb',
    elementTypes: ['overhead-rail', 'overhead-curve', 'cross-rail'],
  },
  {
    id: 'oht-vehicle',
    label: 'OHT VEHICLE',
    color: '#06b6d4',
    elementTypes: ['oht-vehicle'],
  },
  {
    id: 'stocker',
    label: 'STOCKER',
    color: '#a855f7',
    elementTypes: ['stocker'],
  },
  {
    id: 'process-equipment',
    label: 'PROCESS EQUIPMENT',
    color: '#0ea5e9',
    elementTypes: ['rectangle'],
  },
  {
    id: 'stb',
    label: 'SIDE TRACK BUFFER',
    color: '#f97316',
    elementTypes: ['stb'],
  },
  {
    id: 'ohb',
    label: 'OVERHEAD HOIST BUFFER',
    color: '#22c55e',
    elementTypes: ['ohb'],
  },
  {
    id: 'lifter',
    label: 'LIFTER',
    color: '#ef4444',
    elementTypes: ['lifter'],
  },
  {
    id: 'annotation',
    label: 'ANNOTATION',
    color: '#64748b',
    elementTypes: ['rectangle', 'circle', 'text'],
  },
];

const SYSTEM_PRODUCT_IDS = new Set(PRODUCT_TYPES.map((p) => p.id));

function isProtectedProductType(product: ProductTypeDef): boolean {
  return product.isSystem === true || SYSTEM_PRODUCT_IDS.has(product.id);
}

const CATEGORY_COLORS = [
  '#2563eb',
  '#06b6d4',
  '#a855f7',
  '#d946ef',
  '#22c55e',
  '#ef4444',
  '#ec4899',
  '#f97316',
  '#84cc16',
  '#14b8a6',
  '#38bdf8',
  'rainbow',
] as const;

/** Library glyphs — each known icon clones matching TOOLBOX_ITEMS behavior on publish. */
const ICON_LIBRARY = [
  '─',
  '│',
  '⌜',
  '◠',
  '╱',
  '∩',
  '◎',
  'Ｈ',
  '▮',
  '■',
  '▤',
  '▥',
  '↕',
  '▭',
  '○',
  'T',
  'Y',
  'X',
  '◆',
] as const;

function productIdForTemplateType(
  type: ElementType,
  products: ProductTypeDef[]
): string | undefined {
  const exact = products.find((p) => p.id === type);
  if (exact) return exact.id;
  const byElement = products.find((p) => p.elementTypes.includes(type));
  return byElement?.id;
}

const STANDARD_PARAMS: Array<{ key: string; label: string; unit: string; defaultValue: number }> = [
  { key: 'maxSpeed', label: 'Max Speed', unit: 'm/s', defaultValue: 2 },
  { key: 'loadCapacity', label: 'Load Capacity', unit: 'kg', defaultValue: 50 },
  { key: 'throughput', label: 'Throughput', unit: 'FOUP/hr', defaultValue: 60 },
  { key: 'transferTime', label: 'Transfer Time', unit: 's', defaultValue: 8 },
  { key: 'cycleTime', label: 'Cycle Time', unit: 's', defaultValue: 12 },
  { key: 'clearanceHeight', label: 'Clearance Height', unit: 'mm', defaultValue: 200 },
  { key: 'travelHeight', label: 'Travel Height', unit: 'mm', defaultValue: 3200 },
  { key: 'dwellTime', label: 'Dwell Time', unit: 's', defaultValue: 3 },
  { key: 'portCount', label: 'Port Count', unit: 'ports', defaultValue: 2 },
  { key: 'shelfLevels', label: 'Shelf Levels', unit: 'levels', defaultValue: 4 },
  { key: 'acceleration', label: 'Acceleration', unit: 'm/s²', defaultValue: 1 },
  { key: 'minHeadway', label: 'Min Headway', unit: 'mm', defaultValue: 500 },
];

const cloneItems = (items: ToolboxItemType[]): ToolboxItemType[] =>
  items.map((item) => ({
    ...item,
    defaultDimensions: { ...item.defaultDimensions },
    defaultProperties: item.defaultProperties ? { ...item.defaultProperties } : undefined,
    bom: item.bom?.map((row) => ({ ...row })),
    customFields: item.customFields?.map((field) => ({ ...field })),
    enabledParams: item.enabledParams ? [...item.enabledParams] : undefined,
  }));

const groupKeyForItem = (item: ToolboxItemType): string => {
  const railType = item.defaultProperties?.railType;
  if (typeof railType === 'string' && railType) return `rail:${railType}`;
  if (item.sku) return `sku:${item.sku}`;
  return `id:${item.id}`;
};

const groupLabelForItem = (item: ToolboxItemType): string => {
  const railType = item.defaultProperties?.railType;
  if (railType === 'straight')
    return item.defaultRotation === 90 ? 'Vertical Track' : 'AT1 Straight';
  if (railType === 'curve90') return 'AT2 Curve 90°';
  if (railType === 'curve45') return 'AT3 Curve 45°';
  if (railType === 'curve180') return '180° Curve';
  if (railType === 'connector') return 'Connector (H)';
  if (typeof railType === 'string' && railType.startsWith('junction')) return item.name;
  return item.name;
};

function buildEquipmentGroups(items: ToolboxItemType[], product: ProductTypeDef): EquipmentGroup[] {
  const relevant = items.filter((item) =>
    item.productTypeId
      ? item.productTypeId === product.id
      : product.elementTypes.includes(item.type)
  );
  const map = new Map<string, EquipmentGroup>();

  relevant.forEach((item) => {
    const key = groupKeyForItem(item);
    const existing = map.get(key);
    if (existing) {
      existing.items.push(item);
      return;
    }
    map.set(key, {
      id: key,
      label: groupLabelForItem(item),
      icon: item.icon,
      items: [item],
    });
  });

  return Array.from(map.values());
}

type NewToolDraft = {
  name: string;
  productTypeId: string;
  description: string;
  iconSource: IconSource;
  icon: string;
  widthMm: number;
  heightMm: number;
  lengthMm: number;
  weightKg: number;
  enabledParams: Record<string, boolean>;
  paramValues: Record<string, number>;
  customFields: ToolboxCustomField[];
  visibleInToolbox: boolean;
  bom: ToolboxBomLine[];
};

const createEmptyNewTool = (
  productTypeId: string,
  params: Array<{
    key: string;
    label: string;
    unit: string;
    defaultValue: number;
  }> = STANDARD_PARAMS
): NewToolDraft => ({
  name: '',
  productTypeId,
  description: '',
  iconSource: 'library',
  icon: '▮',
  widthMm: 400,
  heightMm: 400,
  lengthMm: 200,
  weightKg: 10,
  enabledParams: Object.fromEntries(params.map((p) => [p.key, false])),
  paramValues: Object.fromEntries(params.map((p) => [p.key, p.defaultValue])),
  customFields: [],
  visibleInToolbox: true,
  bom: [],
});

const toProductTypeDef = (p: ToolboxProductType): ProductTypeDef => ({
  id: p.id,
  label: p.label,
  color: p.color,
  elementTypes: p.elementTypes,
  isSystem: p.isSystem,
});

const ToolboxSettings: React.FC<ToolboxSettingsProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const storedItems = useAppSelector((state) => state.toolbox.items);

  const [draftItems, setDraftItems] = useState<ToolboxItemType[]>(() => cloneItems(storedItems));
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string>(ALL_TOOLS_ID);
  const [configuringId, setConfiguringId] = useState<string | null>(null);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('config');
  const [dirty, setDirty] = useState(false);
  const [productTypesState, setProductTypesState] = useState<ProductTypeDef[]>(PRODUCT_TYPES);
  const [apiStandardParams, setApiStandardParams] = useState<ToolboxStandardParam[] | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [notice, setNotice] = useState<ToolboxNotice | null>(null);
  const clearNotice = useCallback(() => setNotice(null), []);
  const showNotice = useCallback((message: string, tone: NoticeTone) => {
    setNotice({ message, tone });
  }, []);
  useAutoDismissNotice(notice?.message, clearNotice);
  const [addingCategory, setAddingCategory] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryColor, setCategoryColor] = useState<string>(CATEGORY_COLORS[0]);
  const [addToolOpen, setAddToolOpen] = useState(false);
  const [addToolTab, setAddToolTab] = useState<AddToolTab>('setup');
  const [newTool, setNewTool] = useState<NewToolDraft>(() =>
    createEmptyNewTool(PRODUCT_TYPES[0]!.id)
  );
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const wasOpenRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadSeqRef = useRef(0);
  const lastLinkedStraightEditIdRef = useRef<string | null>(null);

  const applyCatalog = useCallback(
    (catalog: {
      productTypes: ToolboxProductType[];
      tools: ToolboxItemType[];
      standardParams: ToolboxStandardParam[];
    }) => {
      const products =
        catalog.productTypes.length > 0
          ? catalog.productTypes.map(toProductTypeDef)
          : PRODUCT_TYPES;
      setProductTypesState(products);
      setDraftItems(cloneItems(catalog.tools));
      setApiStandardParams(catalog.standardParams.length > 0 ? catalog.standardParams : null);
      lastLinkedStraightEditIdRef.current = null;
      dispatch(importToolbox(catalog.tools));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;

    setExpandedProductId(null);
    setSelectedGroupId(null);
    setSelectedProductId(ALL_TOOLS_ID);
    setConfiguringId(null);
    setDrawerTab('config');
    setDirty(false);
    setNotice(null);
    setAddingCategory(false);
    setAddToolOpen(false);
    setConfirmDialog(null);
    setCategoryName('');
    setCategoryColor(CATEGORY_COLORS[0]);
    setDraftItems(cloneItems(storedItems));
    setProductTypesState(PRODUCT_TYPES);
    lastLinkedStraightEditIdRef.current = null;

    const seq = ++loadSeqRef.current;
    setCatalogLoading(true);
    void fetchToolboxCatalog()
      .then((catalog) => {
        if (loadSeqRef.current !== seq) return;
        applyCatalog(catalog);
        showNotice('Toolbox catalog loaded from server.', 'success');
      })
      .catch((err: unknown) => {
        if (loadSeqRef.current !== seq) return;
        setDraftItems(cloneItems(storedItems));
        setProductTypesState(PRODUCT_TYPES);
        showNotice(
          err instanceof Error
            ? `${err.message} Showing local toolbox.`
            : 'Could not load toolbox catalog. Showing local toolbox.',
          'error'
        );
      })
      .finally(() => {
        if (loadSeqRef.current === seq) setCatalogLoading(false);
      });
  }, [isOpen, storedItems, applyCatalog]);

  const productTypes = productTypesState;
  const standardParams = useMemo(() => {
    if (!apiStandardParams?.length) return STANDARD_PARAMS;
    return apiStandardParams.map((p) => ({
      key: p.key,
      label: p.label,
      unit: p.unit ?? '',
      defaultValue: p.defaultValue ?? 0,
    }));
  }, [apiStandardParams]);

  const selectedProduct =
    selectedProductId === ALL_TOOLS_ID
      ? null
      : (productTypes.find((p) => p.id === selectedProductId) ?? null);

  const selectedGroup =
    selectedProduct && selectedGroupId
      ? (buildEquipmentGroups(draftItems, selectedProduct).find((g) => g.id === selectedGroupId) ??
        null)
      : null;

  const configuringItem = configuringId
    ? (draftItems.find((item) => item.id === configuringId) ?? null)
    : null;

  const patchItem = (id: string, updater: (item: ToolboxItemType) => ToolboxItemType) => {
    setDraftItems((prev) => {
      const next = prev.map((item) => (item.id === id ? updater(item) : item));
      if (linkedStraightTrackTwinId(id)) {
        lastLinkedStraightEditIdRef.current = id;
        return syncLinkedStraightTrackTools(next, id);
      }
      return next;
    });
    setDirty(true);
  };

  const askConfirm = (dialog: ConfirmDialogState) => {
    setConfirmDialog(dialog);
  };

  const handleDeleteTool = (id: string, name?: string) => {
    const label = name?.trim() || 'this tool';
    askConfirm({
      title: `Delete “${label}”?`,
      message:
        'This tool will be removed from the draft catalog. Click Save Changes to make it permanent.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: () => {
        setDraftItems((prev) => prev.filter((item) => item.id !== id));
        if (configuringId === id) setConfiguringId(null);
        setDirty(true);
        showNotice(`“${label}” removed from draft. Click Save Changes to publish.`, 'success');
      },
    });
  };

  const handleDeleteCategory = (product: ProductTypeDef) => {
    if (isProtectedProductType(product)) {
      showNotice(`“${product.label}” is a system category and cannot be deleted.`, 'error');
      return;
    }
    const toolCount = draftItems.filter((item) => item.productTypeId === product.id).length;
    askConfirm({
      title: `Delete “${product.label}”?`,
      message:
        toolCount > 0
          ? `This removes the category and its ${toolCount} tool${toolCount === 1 ? '' : 's'} from the draft. Click Save Changes to make it permanent.`
          : 'This removes the category from the draft. Click Save Changes to make it permanent.',
      confirmLabel: 'Delete category',
      tone: 'danger',
      onConfirm: () => {
        setProductTypesState((prev) => prev.filter((p) => p.id !== product.id));
        setDraftItems((prev) => prev.filter((item) => item.productTypeId !== product.id));
        if (selectedProductId === product.id) {
          setSelectedProductId(ALL_TOOLS_ID);
          setSelectedGroupId(null);
          setExpandedProductId(null);
        }
        if (expandedProductId === product.id) setExpandedProductId(null);
        if (configuringItem?.productTypeId === product.id) setConfiguringId(null);
        setDirty(true);
        showNotice(
          `Category “${product.label}” removed from draft. Click Save Changes to publish.`,
          'success'
        );
      },
    });
  };

  const handleBack = () => {
    if (!dirty) {
      onClose();
      return;
    }
    askConfirm({
      title: 'Discard unsaved changes?',
      message: 'You have unsaved toolbox edits. Leaving now will discard them.',
      confirmLabel: 'Discard',
      tone: 'warning',
      onConfirm: () => onClose(),
    });
  };

  const runResetAll = () => {
    setCatalogSaving(true);
    void resetToolboxCatalog('all')
      .then((catalog) => {
        applyCatalog(catalog);
        setDirty(false);
        setConfiguringId(null);
        setSelectedGroupId(null);
        setSelectedProductId(ALL_TOOLS_ID);
        setAddToolOpen(false);
        showNotice('Toolbox catalog reset to server defaults.', 'success');
      })
      .catch((err: unknown) => {
        // Offline / API failure — fall back to local seed so the editor stays usable.
        setDraftItems(cloneItems(TOOLBOX_ITEMS));
        setProductTypesState(PRODUCT_TYPES);
        setDirty(true);
        setConfiguringId(null);
        setSelectedGroupId(null);
        setSelectedProductId(ALL_TOOLS_ID);
        setAddToolOpen(false);
        showNotice(
          err instanceof Error
            ? `${err.message} Local defaults loaded — click Save Changes when online.`
            : 'Reset failed. Local defaults loaded.',
          'error'
        );
      })
      .finally(() => setCatalogSaving(false));
  };

  const handleResetAll = () => {
    askConfirm({
      title: 'Reset all tools?',
      message:
        'This restores the default toolbox catalog on the server and replaces your current draft.',
      confirmLabel: 'Reset all',
      tone: 'warning',
      onConfirm: runResetAll,
    });
  };

  const handleSave = () => {
    const linkedSourceId = lastLinkedStraightEditIdRef.current;
    const toolsToSave = linkedSourceId
      ? syncLinkedStraightTrackTools(draftItems, linkedSourceId)
      : draftItems;
    if (linkedSourceId) {
      setDraftItems(toolsToSave);
    }
    setCatalogSaving(true);
    void saveToolboxCatalog(
      productTypes.map((p) => ({
        id: p.id,
        label: p.label,
        color: p.color,
        elementTypes: p.elementTypes,
      })),
      toolsToSave,
      linkedSourceId ? { linkedStraightTrackSourceId: linkedSourceId } : undefined
    )
      .then((catalog) => {
        lastLinkedStraightEditIdRef.current = null;
        applyCatalog(catalog);
        setDirty(false);
        showNotice('Toolbox catalog saved to server.', 'success');
      })
      .catch((err: unknown) => {
        // Keep the draft local so the user does not lose work if the API is down.
        dispatch(importToolbox(toolsToSave));
        setDirty(false);
        showNotice(
          err instanceof Error
            ? `${err.message} Saved locally in this session only.`
            : 'Save failed. Changes kept locally in this session only.',
          'error'
        );
      })
      .finally(() => setCatalogSaving(false));
  };

  const openAddTool = () => {
    const productId =
      selectedProductId !== ALL_TOOLS_ID && selectedProductId
        ? selectedProductId
        : (productTypes[0]?.id ?? PRODUCT_TYPES[0]!.id);
    setNewTool(createEmptyNewTool(productId, standardParams));
    setAddToolTab('setup');
    setConfiguringId(null);
    setAddToolOpen(true);
  };

  const handleAddCategory = () => {
    if (!categoryName.trim()) return;
    const label = categoryName.trim().toUpperCase();
    const color =
      categoryColor === 'rainbow'
        ? CATEGORY_COLORS[Math.floor(Math.random() * (CATEGORY_COLORS.length - 1))]!
        : categoryColor;

    setCatalogSaving(true);
    void createToolboxProductType({
      label,
      color,
      elementTypes: ['rectangle'],
    })
      .then((created) => {
        const def = toProductTypeDef(created);
        setProductTypesState((prev) => [...prev, def]);
        setAddingCategory(false);
        setCategoryName('');
        setCategoryColor(CATEGORY_COLORS[0]);
        setSelectedProductId(def.id);
        setExpandedProductId(def.id);
        setSelectedGroupId(null);
        setDirty(true);
        showNotice(`Category “${def.label}” created. Add tools, then Save Changes.`, 'success');
      })
      .catch((err: unknown) => {
        // Keep drafting offline with a local id; Save Changes will upsert via PUT.
        const id = `custom-${generateId()}`;
        const def: ProductTypeDef = {
          id,
          label,
          color,
          elementTypes: ['rectangle'],
        };
        setProductTypesState((prev) => [...prev, def]);
        setAddingCategory(false);
        setCategoryName('');
        setCategoryColor(CATEGORY_COLORS[0]);
        setSelectedProductId(id);
        setExpandedProductId(id);
        setSelectedGroupId(null);
        setDirty(true);
        showNotice(
          err instanceof Error
            ? `${err.message} Category kept in draft — Save Changes when online.`
            : 'Category kept in draft locally.',
          'error'
        );
      })
      .finally(() => setCatalogSaving(false));
  };

  const handlePublishNewTool = () => {
    const product = productTypes.find((p) => p.id === newTool.productTypeId) ?? PRODUCT_TYPES[0]!;
    const fallbackType = product.elementTypes[0] ?? 'rectangle';
    const template = resolveToolboxTemplateByIcon(newTool.icon, {
      preferredElementTypes: product.elementTypes,
    });
    const mmToCanvas = (mm: number) => Math.max(4, mm / 20);
    const enabledKeys = standardParams
      .filter((p) => newTool.enabledParams[p.key])
      .map((p) => p.key);
    const userProps: Record<string, unknown> = {};
    enabledKeys.forEach((key) => {
      userProps[key] = newTool.paramValues[key];
    });

    const behavior = behaviorFromIconTemplate(template, fallbackType, userProps);
    const type = behavior.type;

    const item: ToolboxItemType = {
      id: newToolboxToolId(),
      productTypeId: product.id,
      type,
      name: newTool.name.trim() || `New ${CLASS_LABELS[type] ?? type}`,
      icon: newTool.icon || '◆',
      description: newTool.description.trim() || undefined,
      defaultDimensions: {
        width: mmToCanvas(newTool.widthMm),
        height: mmToCanvas(newTool.heightMm),
        depth: mmToCanvas(newTool.lengthMm),
      },
      category: product.label,
      sku: newTool.name.trim() || product.label,
      defaultProperties: behavior.defaultProperties,
      defaultRotation: behavior.defaultRotation,
      bom: newTool.bom,
      weightKg: newTool.weightKg,
      visibleInToolbox: newTool.visibleInToolbox,
      customFields: newTool.customFields,
      enabledParams: enabledKeys,
    };

    setDraftItems((prev) => [...prev, item]);
    setDirty(true);
    setAddToolOpen(false);
    setExpandedProductId(product.id);
    setSelectedProductId(product.id);
    const groups = buildEquipmentGroups([...draftItems, item], product);
    const group = groups.find((g) => g.items.some((i) => i.id === item.id));
    setSelectedGroupId(group?.id ?? groupKeyForItem(item));
    const behaviorHint = template
      ? ` Behaves like ${template.name} (${CLASS_LABELS[type] ?? type}).`
      : '';
    showNotice(
      `“${item.name}” added to draft.${behaviorHint} Click Save Changes to publish.`,
      'success'
    );
  };

  const canPublishNewTool =
    newTool.name.trim().length > 0 && Boolean(newTool.productTypeId) && Boolean(newTool.icon);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-[#f1f5f9] pb-8">
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          tone={confirmDialog.tone}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={() => {
            const action = confirmDialog.onConfirm;
            setConfirmDialog(null);
            action();
          }}
        />
      )}

      <header className="relative flex min-h-14 shrink-0 items-center justify-between border-b border-slate-900/10 bg-white/95 px-5 py-2.5">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <ChevronLeftIcon />
          Back to Editor
        </button>

        <div className="text-center">
          <h1 className="text-[15px] font-semibold text-slate-900">Tools Configuration</h1>
          <p className="text-[11px] text-slate-500">Set properties and defaults for each tool</p>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleResetAll}
            disabled={catalogLoading || catalogSaving}
            className="rounded-md border border-slate-900/10 px-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Reset All
          </button>
          <button
            type="button"
            onClick={openAddTool}
            disabled={catalogLoading || catalogSaving}
            className="rounded-md bg-gradient-to-br from-violet-600 to-violet-700 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40"
          >
            + New Tool
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || catalogLoading || catalogSaving}
            className="rounded-md bg-gradient-to-br from-blue-600 to-blue-700 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {catalogSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {notice && (
          <div
            className={`absolute right-5 top-full z-50 mt-2 max-w-sm rounded-lg border-2 bg-white px-3.5 py-2.5 text-sm shadow-lg ${
              notice.tone === 'error'
                ? 'border-red-500 text-red-800'
                : 'border-green-500 text-green-800'
            }`}
            role="status"
          >
            <div className="flex items-start gap-3">
              <span className="flex-1">{notice.message}</span>
              <button
                type="button"
                aria-label="Dismiss message"
                onClick={() => setNotice(null)}
                className={
                  notice.tone === 'error'
                    ? 'text-red-400 hover:text-red-700'
                    : 'text-green-500 hover:text-green-800'
                }
              >
                ×
              </button>
            </div>
          </div>
        )}
      </header>

      {catalogLoading && (
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-2 text-xs text-slate-600">
          Loading toolbox catalog…
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="flex w-[182px] shrink-0 flex-col overflow-y-auto border-r border-slate-900/10 bg-white/95">
          <div className="p-2.5">
            <div className="px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.9px] text-slate-500">
              Product Type
            </div>

            <button
              type="button"
              onClick={() => {
                setSelectedProductId(ALL_TOOLS_ID);
                setExpandedProductId(null);
                setSelectedGroupId(null);
                setConfiguringId(null);
              }}
              className={`mb-0.5 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors ${
                selectedProductId === ALL_TOOLS_ID
                  ? 'bg-blue-600/10 text-blue-600'
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <ColorChevron
                color={selectedProductId === ALL_TOOLS_ID ? '#2563eb' : '#94a3b8'}
                open={false}
              />
              <span className="min-w-0 flex-1 truncate text-xs font-medium">All Tools</span>
              <span className="text-[10px] opacity-60">{draftItems.length}</span>
            </button>

            <div className="space-y-0.5">
              {productTypes.map((product) => {
                const groups = buildEquipmentGroups(draftItems, product);
                const count = groups.length;
                const expanded = expandedProductId === product.id;
                const activeProduct = selectedProductId === product.id;

                const canDeleteCategory = !isProtectedProductType(product);

                return (
                  <div key={product.id} className="group/category">
                    <div
                      className={`flex w-full items-center gap-1 rounded-md transition-colors ${
                        activeProduct && !selectedGroupId
                          ? 'bg-blue-600/10 text-blue-600'
                          : 'text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedProductId((prev) => (prev === product.id ? null : product.id));
                          setSelectedProductId(product.id);
                          setSelectedGroupId(null);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left"
                      >
                        <ColorChevron color={product.color} open={expanded} />
                        <span className="min-w-0 flex-1 truncate text-[11px] font-medium uppercase tracking-wide">
                          {product.label}
                        </span>
                        <span className="text-[10px] opacity-60">{count}</span>
                      </button>
                      {canDeleteCategory && (
                        <button
                          type="button"
                          title={`Delete ${product.label}`}
                          aria-label={`Delete category ${product.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(product);
                          }}
                          className="mr-1.5 rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover/category:opacity-100 focus:opacity-100"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>

                    {expanded && (
                      <div className="mb-1 ml-1 space-y-0.5 border-l border-slate-100 pl-2">
                        {groups.map((group) => {
                          const active = selectedGroupId === group.id;
                          return (
                            <button
                              key={group.id}
                              type="button"
                              onClick={() => {
                                setSelectedProductId(product.id);
                                setSelectedGroupId(group.id);
                                setConfiguringId(null);
                                setAddToolOpen(false);
                              }}
                              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
                                active
                                  ? 'border-l-2 border-blue-600 bg-blue-50 text-blue-700'
                                  : 'border-l-2 border-transparent text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              <span className="flex size-5 shrink-0 items-center justify-center overflow-hidden text-[11px]">
                                <ToolGlyph icon={group.icon} />
                              </span>
                              <span className="min-w-0 flex-1 truncate text-[11px] font-medium">
                                {group.label}
                              </span>
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                  active
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-slate-100 text-slate-500'
                                }`}
                              >
                                {group.items.length}
                              </span>
                            </button>
                          );
                        })}
                        {groups.length === 0 && (
                          <p className="px-2 py-1.5 text-[10px] text-slate-400">No tools yet</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-3 border-t border-slate-100 pt-3">
              {addingCategory ? (
                <div className="rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm">
                  <label className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-slate-500">
                    Name
                  </label>
                  <input
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="e.g. Conveyor Belt"
                    className="mb-3 w-full rounded-md border-0 bg-slate-100 px-2.5 py-1.5 text-xs text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                  <label className="mb-1.5 block text-[9px] font-bold uppercase tracking-wide text-slate-500">
                    Color
                  </label>
                  <div className="mb-3 grid grid-cols-6 gap-1.5">
                    {CATEGORY_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setCategoryColor(color)}
                        className={`size-5 rounded-full border-2 ${
                          categoryColor === color ? 'border-slate-700' : 'border-transparent'
                        }`}
                        style={
                          color === 'rainbow'
                            ? {
                                background:
                                  'conic-gradient(#2563eb, #22c55e, #eab308, #ef4444, #a855f7, #2563eb)',
                              }
                            : { backgroundColor: color }
                        }
                        aria-label={`Color ${color}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setAddingCategory(false);
                        setCategoryName('');
                      }}
                      className="px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      disabled={!categoryName.trim()}
                      className="rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-40"
                    >
                      Add
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingCategory(true)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-blue-300/80 px-3 py-2 text-[11px] font-semibold text-blue-600 transition-colors hover:bg-blue-50"
                >
                  <span className="text-sm leading-none">+</span>
                  New Category
                </button>
              )}
            </div>
          </div>
        </aside>

        <main className="relative min-w-0 flex-1 overflow-y-auto">
          {!selectedGroup ? (
            <EmptyState />
          ) : (
            <div className="p-8">
              <div className="mb-6 flex items-end justify-between gap-4">
                <div>
                  <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    <span
                      className="size-1.5 rounded-full"
                      style={{ backgroundColor: selectedProduct?.color }}
                    />
                    {selectedProduct?.label}
                  </div>
                  <h2 className="text-2xl font-semibold text-slate-900">{selectedGroup.label}</h2>
                </div>
                <div className="text-xs font-medium text-slate-500">
                  {selectedGroup.items.length} variant
                  {selectedGroup.items.length === 1 ? '' : 's'}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {selectedGroup.items.map((item) => (
                  <VariantCard
                    key={item.id}
                    item={item}
                    onConfigure={() => {
                      setAddToolOpen(false);
                      setConfiguringId(item.id);
                      setDrawerTab('config');
                    }}
                    onDelete={() => handleDeleteTool(item.id, item.name)}
                  />
                ))}
              </div>
            </div>
          )}
        </main>

        {addToolOpen && (
          <AddNewToolDrawer
            draft={newTool}
            tab={addToolTab}
            productTypes={productTypes}
            standardParams={standardParams}
            canPublish={canPublishNewTool}
            fileInputRef={fileInputRef}
            onTabChange={setAddToolTab}
            onChange={setNewTool}
            onClose={() => setAddToolOpen(false)}
            onPublish={handlePublishNewTool}
          />
        )}

        {!addToolOpen && configuringItem && (
          <ConfigureDrawer
            item={configuringItem}
            tab={drawerTab}
            onTabChange={setDrawerTab}
            onClose={() => setConfiguringId(null)}
            onChange={(next) => patchItem(configuringItem.id, () => next)}
            onDelete={() => handleDeleteTool(configuringItem.id, configuringItem.name)}
          />
        )}
      </div>

      <footer className="fixed inset-x-0 bottom-0 z-[110] flex h-8 items-center border-t border-slate-200 bg-white px-4 text-[11px] text-slate-500 select-none">
        <PoweredByPathfinder className="shrink-0 text-slate-600" />
      </footer>
    </div>
  );
};

const ConfirmDialog: React.FC<{
  title: string;
  message: string;
  confirmLabel: string;
  tone: ConfirmTone;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, message, confirmLabel, tone, onCancel, onConfirm }) => {
  const isDanger = tone === 'danger';
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm"
      onClick={onCancel}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="toolbox-confirm-title"
        aria-describedby="toolbox-confirm-message"
        className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              isDanger ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
            }`}
          >
            {isDanger ? <TrashIcon /> : <WarningIcon />}
          </div>
          <div>
            <div id="toolbox-confirm-title" className="text-base font-semibold text-slate-900">
              {title}
            </div>
            <div id="toolbox-confirm-message" className="mt-1 text-sm leading-5 text-slate-500">
              {message}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold text-white transition-colors ${
              isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="flex h-full flex-col items-center justify-center px-6 text-center">
    <div className="mb-5 flex size-14 items-center justify-center rounded-2xl bg-black/[0.04] text-slate-400">
      <SlidersIcon />
    </div>
    <h2 className="text-[15px] font-semibold text-slate-900">Select an Equipment</h2>
    <p className="mt-2 max-w-[280px] text-[13px] leading-relaxed text-slate-500">
      Expand a <span className="font-bold">Product Type</span> in the left panel and click an{' '}
      <span className="font-bold">Equipment Type</span> to browse its variants.
    </p>
    <div className="mt-5 flex items-center gap-1.5 text-xs">
      <span className="rounded-md border border-slate-900/10 bg-[#e8edf4] px-2.5 py-1 text-slate-500">
        Product Type
      </span>
      <ChevronRightIcon />
      <span className="rounded-md border border-slate-900/10 bg-[#e8edf4] px-2.5 py-1 text-slate-500">
        Equipment Type
      </span>
      <ChevronRightIcon />
      <span className="rounded-md border border-blue-400/40 bg-blue-600/5 px-2.5 py-1 font-semibold text-blue-600">
        Equipment
      </span>
    </div>
  </div>
);

const VariantCard: React.FC<{
  item: ToolboxItemType;
  onConfigure: () => void;
  onDelete: () => void;
}> = ({ item, onConfigure, onDelete }) => (
  <div className="group relative flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
    <button
      type="button"
      title={`Delete ${item.name}`}
      aria-label={`Delete ${item.name}`}
      onClick={onDelete}
      className="absolute right-3 top-3 rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
    >
      <TrashIcon />
    </button>
    <div className="mb-3 flex size-10 items-center justify-center rounded-lg border border-slate-100 bg-slate-50 text-lg text-slate-700">
      <ToolGlyph icon={item.icon} />
    </div>
    <div className="pr-8 text-sm font-semibold text-slate-900">{item.name}</div>
    <p className="mt-1 min-h-[2.5rem] text-xs leading-relaxed text-slate-500">
      {item.description || item.sku || CLASS_LABELS[item.type]}
    </p>
    <button
      type="button"
      onClick={onConfigure}
      className="mt-3 self-start text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700"
    >
      Configure →
    </button>
  </div>
);

const AddNewToolDrawer: React.FC<{
  draft: NewToolDraft;
  tab: AddToolTab;
  productTypes: ProductTypeDef[];
  standardParams: Array<{ key: string; label: string; unit: string; defaultValue: number }>;
  canPublish: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onTabChange: (tab: AddToolTab) => void;
  onChange: (draft: NewToolDraft) => void;
  onClose: () => void;
  onPublish: () => void;
}> = ({
  draft,
  tab,
  productTypes,
  standardParams,
  canPublish,
  fileInputRef,
  onTabChange,
  onChange,
  onClose,
  onPublish,
}) => {
  const product = productTypes.find((p) => p.id === draft.productTypeId);
  const iconTemplate = resolveToolboxTemplateByIcon(draft.icon, {
    preferredElementTypes: product?.elementTypes,
  });
  const patch = (partial: Partial<NewToolDraft>) => onChange({ ...draft, ...partial });

  const selectLibraryIcon = (icon: string) => {
    const template = resolveToolboxTemplateByIcon(icon, {
      preferredElementTypes: product?.elementTypes,
    });
    const nextProductId = template
      ? (productIdForTemplateType(template.type, productTypes) ?? draft.productTypeId)
      : draft.productTypeId;
    patch({ icon, productTypeId: nextProductId });
  };

  return (
    <aside className="flex w-[480px] shrink-0 flex-col border-l border-slate-900/10 bg-white shadow-[-8px_0_24px_-12px_rgba(15,23,42,0.15)]">
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-4">
        <div>
          <div className="text-sm font-semibold text-slate-900">Add New Tool</div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            Configure and publish a custom tool to your toolbox
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex border-b border-slate-100 px-4">
        <DrawerTabButton active={tab === 'setup'} onClick={() => onTabChange('setup')}>
          Tool Setup
        </DrawerTabButton>
        <DrawerTabButton active={tab === 'bom'} onClick={() => onTabChange('bom')}>
          BOM Components
        </DrawerTabButton>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {tab === 'setup' ? (
          <div className="space-y-6">
            <Section title="Tool Identity">
              <label className="mb-1 block text-[11px] font-semibold text-slate-700">
                Equipment <span className="text-red-500">*</span>
              </label>
              <input
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="e.g. Custom Buffer Station"
                className="mb-3 w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              <label className="mb-1 block text-[11px] font-semibold text-slate-700">
                Product Type <span className="text-red-500">*</span>
              </label>
              <select
                value={draft.productTypeId}
                onChange={(e) => patch({ productTypeId: e.target.value })}
                className="mb-3 w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                {productTypes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <label className="mb-1 block text-[11px] font-semibold text-slate-700">
                Description
              </label>
              <textarea
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder="Briefly describe what this tool represents in the layout..."
                rows={3}
                className="w-full resize-none rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </Section>

            <Section
              title="Icon / Image"
              hint="Library icons reuse the matching builtin tool’s placement behavior (e.g. ▮ = OHT)."
            >
              <div className="mb-3 flex rounded-lg bg-slate-100 p-0.5">
                <button
                  type="button"
                  onClick={() => patch({ iconSource: 'library' })}
                  className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    draft.iconSource === 'library'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500'
                  }`}
                >
                  From Library
                </button>
                <button
                  type="button"
                  onClick={() => patch({ iconSource: 'upload' })}
                  className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    draft.iconSource === 'upload'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500'
                  }`}
                >
                  Upload Image
                </button>
              </div>

              {draft.iconSource === 'library' ? (
                <div className="mb-3 grid grid-cols-6 gap-1.5">
                  {ICON_LIBRARY.map((icon, index) => {
                    const selected = draft.icon === icon;
                    const match = resolveToolboxTemplateByIcon(icon);
                    return (
                      <button
                        key={`${icon}-${index}`}
                        type="button"
                        title={match ? `Behaves like ${match.name}` : undefined}
                        onClick={() => selectLibraryIcon(icon)}
                        className={`flex size-10 items-center justify-center rounded-lg border text-base transition-colors ${
                          selected
                            ? 'border-blue-500 bg-blue-50 text-blue-600'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {icon}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mb-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.svg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        if (typeof reader.result === 'string') {
                          patch({ icon: reader.result });
                        }
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center transition-colors hover:border-blue-300 hover:bg-blue-50/40"
                  >
                    <span className="text-xl text-slate-400">+</span>
                    <span className="text-xs font-medium text-slate-600">
                      Drop image or SVG here
                    </span>
                    <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-blue-600">
                      Browse files
                    </span>
                  </button>
                </div>
              )}

              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
                  Preview
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50 text-lg text-blue-600">
                    <ToolGlyph icon={draft.icon} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {draft.name.trim() || 'Equipment Name'}
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      {product?.label ?? 'OVERHEAD RAIL'}
                    </div>
                    {iconTemplate ? (
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        Behaves like {iconTemplate.name}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </Section>

            <Section
              title="Physical Dimensions"
              hint="Footprint and weight of the physical equipment."
            >
              <div className="grid grid-cols-2 gap-3">
                <UnitField
                  label="Width"
                  unit="mm"
                  value={draft.widthMm}
                  onChange={(widthMm) => patch({ widthMm })}
                />
                <UnitField
                  label="Height"
                  unit="mm"
                  value={draft.heightMm}
                  onChange={(heightMm) => patch({ heightMm })}
                />
                <UnitField
                  label="Length"
                  unit="mm"
                  value={draft.lengthMm}
                  onChange={(lengthMm) => patch({ lengthMm })}
                />
                <UnitField
                  label="Weight"
                  unit="kg"
                  value={draft.weightKg}
                  onChange={(weightKg) => patch({ weightKg })}
                />
              </div>
            </Section>

            <Section
              title="Standard Parameters"
              hint="Toggle on each parameter you want to configure for this tool."
            >
              <div className="space-y-2">
                {standardParams.map((param) => {
                  const enabled = Boolean(draft.enabledParams[param.key]);
                  return (
                    <div
                      key={param.key}
                      className="flex items-center gap-3 rounded-lg border border-slate-100 px-2.5 py-2"
                    >
                      <Toggle
                        on={enabled}
                        onChange={(on) =>
                          patch({
                            enabledParams: { ...draft.enabledParams, [param.key]: on },
                          })
                        }
                      />
                      <span className="min-w-0 flex-1 text-xs font-medium text-slate-700">
                        {param.label}
                      </span>
                      <div className="relative w-24">
                        <input
                          type="number"
                          disabled={!enabled}
                          value={draft.paramValues[param.key] ?? param.defaultValue}
                          onChange={(e) =>
                            patch({
                              paramValues: {
                                ...draft.paramValues,
                                [param.key]: Number(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full rounded-md border-0 bg-slate-100 py-1.5 pl-2 pr-10 text-xs outline-none disabled:text-slate-400"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-slate-400">
                          {param.unit}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section
              title="Custom Configuration Fields"
              hint="Add any extra properties users can configure for this tool."
            >
              <div className="space-y-2">
                {draft.customFields.map((field) => (
                  <div key={field.id} className="flex items-center gap-2">
                    <input
                      value={field.label}
                      placeholder="Label"
                      onChange={(e) =>
                        patch({
                          customFields: draft.customFields.map((f) =>
                            f.id === field.id ? { ...f, label: e.target.value } : f
                          ),
                        })
                      }
                      className="flex-1 rounded-md bg-slate-100 px-2 py-1.5 text-xs outline-none"
                    />
                    <input
                      value={field.value}
                      placeholder="Value"
                      onChange={(e) =>
                        patch({
                          customFields: draft.customFields.map((f) =>
                            f.id === field.id ? { ...f, value: e.target.value } : f
                          ),
                        })
                      }
                      className="w-20 rounded-md bg-slate-100 px-2 py-1.5 text-xs outline-none"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        patch({
                          customFields: draft.customFields.filter((f) => f.id !== field.id),
                        })
                      }
                      className="text-slate-400 hover:text-red-500"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  patch({
                    customFields: [
                      ...draft.customFields,
                      { id: generateId(), label: '', value: '' },
                    ],
                  })
                }
                className="mt-2 w-full rounded-lg border border-dashed border-blue-300 py-2.5 text-xs font-semibold text-blue-600 hover:bg-blue-50"
              >
                + Add Configuration Field
              </button>
            </Section>

            <Section title="Toolbox Settings">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-3">
                <div>
                  <div className="text-xs font-semibold text-slate-800">Visible by default</div>
                  <div className="text-[10px] text-slate-500">
                    Show this tool in the toolbox when added.
                  </div>
                </div>
                <Toggle
                  on={draft.visibleInToolbox}
                  onChange={(visibleInToolbox) => patch({ visibleInToolbox })}
                />
              </div>
            </Section>
          </div>
        ) : (
          <BomEditor bom={draft.bom} onChange={(bom) => patch({ bom })} />
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canPublish}
          onClick={onPublish}
          className="rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          Add to Toolbox
        </button>
      </div>
    </aside>
  );
};

const ConfigureDrawer: React.FC<{
  item: ToolboxItemType;
  tab: DrawerTab;
  onTabChange: (tab: DrawerTab) => void;
  onClose: () => void;
  onChange: (item: ToolboxItemType) => void;
  onDelete: () => void;
}> = ({ item, tab, onTabChange, onClose, onChange, onDelete }) => {
  const fields = COMPONENT_SCHEMAS[item.type] ?? [];
  const { processTypes } = useProjectProcessTypes([
    item.defaultProperties?.processType,
    item.defaultProperties?.toolType,
  ]);
  const bom = item.bom ?? [];

  const setProp = (key: string, value: unknown) => {
    onChange({
      ...item,
      defaultProperties: {
        ...(item.defaultProperties ?? {}),
        [key]: value,
      },
    });
  };

  return (
    <aside className="flex w-[480px] shrink-0 flex-col border-l border-slate-900/10 bg-white shadow-[-8px_0_24px_-12px_rgba(15,23,42,0.15)]">
      <div className="flex items-start gap-3 border-b border-slate-100 px-4 py-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-slate-100 bg-slate-50 text-lg">
          <ToolGlyph icon={item.icon} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-900">{item.name}</div>
          <div className="text-[11px] text-slate-500">
            {item.category ?? CLASS_LABELS[item.type]} · {Math.max(fields.length, 1)} fields
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close configuration"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex border-b border-slate-100 px-4">
        <DrawerTabButton active={tab === 'config'} onClick={() => onTabChange('config')}>
          Configuration
        </DrawerTabButton>
        <DrawerTabButton active={tab === 'bom'} onClick={() => onTabChange('bom')}>
          BOM Components
        </DrawerTabButton>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {tab === 'config' ? (
          <div className="space-y-4">
            <FieldRow label="Name" hint="Display name in the toolbox">
              <input
                value={item.name}
                onChange={(e) => onChange({ ...item, name: e.target.value })}
                className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </FieldRow>
            <FieldRow label="Description">
              <input
                value={item.description ?? ''}
                onChange={(e) => onChange({ ...item, description: e.target.value })}
                className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </FieldRow>
            {fields
              .filter((field) => field.key !== 'junctionWaitSec')
              .map((field) => (
              <SchemaField
                key={field.key}
                field={
                  field.key === 'processType' && field.type === 'select'
                    ? { ...field, options: processTypes }
                    : field
                }
                value={item.defaultProperties?.[field.key]}
                onChange={(value) => setProp(field.key, value)}
              />
            ))}
          </div>
        ) : (
          <BomEditor
            bom={bom}
            onChange={(next) => onChange({ ...item, bom: next })}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
        >
          <TrashIcon />
          Delete tool
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
        >
          Done
        </button>
      </div>
    </aside>
  );
};

const BOM_FIELD_CLASS =
  'min-w-0 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-blue-400';

const createEmptyBomLine = (): ToolboxBomLine => ({
  id: generateId(),
  name: '',
  qty: 1,
  category: '',
  description: '',
  unit: 'pcs',
});

const BomEditor: React.FC<{
  bom: ToolboxBomLine[];
  onChange: (bom: ToolboxBomLine[]) => void;
}> = ({ bom, onChange }) => {
  const patchRow = (id: string, partial: Partial<ToolboxBomLine>) =>
    onChange(bom.map((row) => (row.id === id ? { ...row, ...partial } : row)));

  return (
    <div className="space-y-4">
      <div className="text-[9px] font-bold uppercase tracking-[0.9px] text-slate-500">
        Installation Components
      </div>
      <div className="space-y-3">
        {bom.map((row) => (
          <div key={row.id} className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 flex items-start justify-between gap-2">
              <label className="min-w-0 flex-1">
                <span className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-slate-500">
                  Name
                </span>
                <input
                  value={row.name}
                  placeholder="Component name..."
                  onChange={(e) => patchRow(row.id, { name: e.target.value })}
                  className={BOM_FIELD_CLASS}
                />
              </label>
              <button
                type="button"
                onClick={() => onChange(bom.filter((r) => r.id !== row.id))}
                className="mt-5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-50 hover:text-red-500"
                aria-label="Remove item"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_88px_minmax(0,1.2fr)] gap-2">
              <label>
                <span className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-slate-500">
                  Category
                </span>
                <input
                  value={row.category ?? ''}
                  placeholder="Optional"
                  onChange={(e) => patchRow(row.id, { category: e.target.value })}
                  className={BOM_FIELD_CLASS}
                />
              </label>
              <label>
                <span className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-slate-500">
                  Qty
                </span>
                <input
                  type="number"
                  min={0}
                  value={row.qty}
                  onChange={(e) => patchRow(row.id, { qty: Number(e.target.value) || 0 })}
                  className={`${BOM_FIELD_CLASS} text-right`}
                />
              </label>
              <label>
                <span className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-slate-500">
                  Unit
                </span>
                <select
                  value={row.unit}
                  onChange={(e) => patchRow(row.id, { unit: e.target.value as ToolboxBomUnit })}
                  className={BOM_FIELD_CLASS}
                >
                  {TOOLBOX_BOM_UNITS.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="mt-2 block">
              <span className="mb-1 block text-[9px] font-bold uppercase tracking-wide text-slate-500">
                Description
              </span>
              <textarea
                value={row.description ?? ''}
                placeholder="Describe this component..."
                rows={2}
                onChange={(e) => patchRow(row.id, { description: e.target.value })}
                className={`${BOM_FIELD_CLASS} resize-none`}
              />
            </label>
          </div>
        ))}
        {bom.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-xs text-slate-400">
            No BOM items yet
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange([...bom, createEmptyBomLine()])}
        className="w-full rounded-lg border border-blue-300 py-2 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50"
      >
        + Add item
      </button>
    </div>
  );
};

const SchemaField: React.FC<{
  field: ComponentField;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ field, value, onChange }) => {
  const hint =
    field.key === 'lengthMeters'
      ? 'Physical length of the rail segment'
      : field.key === 'heightM'
        ? 'Vertical clearance / rail height'
        : undefined;

  return (
    <FieldRow label={field.label} hint={hint}>
      {field.type === 'select' ? (
        <select
          value={String(value ?? field.options?.[0] ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/30"
        >
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : field.type === 'boolean' ? (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Enabled
        </label>
      ) : (
        <div className="relative">
          <input
            type={field.type === 'number' ? 'number' : 'text'}
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(e) =>
              onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)
            }
            className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 pr-12 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/30"
          />
          {field.unit && (
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              {field.unit}
            </span>
          )}
        </div>
      )}
    </FieldRow>
  );
};

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <section>
    <div className="mb-2 text-[9px] font-bold uppercase tracking-[0.9px] text-slate-500">
      {title}
    </div>
    {hint && <p className="mb-2 text-[11px] leading-snug text-slate-400">{hint}</p>}
    {children}
  </section>
);

const UnitField: React.FC<{
  label: string;
  unit: string;
  value: number;
  onChange: (value: number) => void;
}> = ({ label, unit, value, onChange }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold text-slate-700">{label}</label>
    <div className="relative">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
        {unit}
      </span>
    </div>
  </div>
);

const FieldRow: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="grid grid-cols-[1fr_1.1fr] items-start gap-3">
    <div className="pt-2">
      <div className="text-xs font-medium text-slate-700">{label}</div>
      {hint && <div className="mt-0.5 text-[10px] leading-snug text-slate-400">{hint}</div>}
    </div>
    <div>{children}</div>
  </div>
);

const DrawerTabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative mr-4 py-2.5 text-[11px] font-semibold transition-colors ${
      active ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
    }`}
  >
    {children}
    {active && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-blue-600" />}
  </button>
);

const Toggle: React.FC<{ on: boolean; onChange: (on: boolean) => void }> = ({ on, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={() => onChange(!on)}
    className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
      on ? 'bg-blue-600' : 'bg-slate-300'
    }`}
  >
    <span
      className={`absolute top-0.5 size-4 rounded-full bg-white shadow transition-transform ${
        on ? 'left-4' : 'left-0.5'
      }`}
    />
  </button>
);

const ColorChevron: React.FC<{ color: string; open: boolean }> = ({ color, open }) => (
  <span
    className={`inline-block size-0 border-y-[4px] border-y-transparent border-l-[6px] transition-transform ${
      open ? 'rotate-90' : ''
    }`}
    style={{ borderLeftColor: color }}
    aria-hidden
  />
);

const ToolGlyph: React.FC<{ icon: string }> = ({ icon }) => {
  const isImage = icon.startsWith('data:image') || icon.startsWith('http');
  if (isImage) {
    return <img src={icon} alt="" className="size-full object-contain p-0.5" />;
  }
  return <span aria-hidden>{icon}</span>;
};

const ChevronLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 11v6M14 11v6" strokeLinecap="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path
      d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SlidersIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
  >
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" strokeLinecap="round" />
    <path d="M2 14h4M10 8h4M18 16h4" strokeLinecap="round" />
  </svg>
);

export default ToolboxSettings;
